/**
 * Keeps the Ask Agent Loop conversation (Oct 2026).
 *
 * Sits around the whole app in App.tsx, so the conversation is still
 * there when someone goes to a case and comes back to /ask. The page
 * itself is pages/Ask.tsx. Anything can open it with useAsk().open().
 */

import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AskContext, AskHttpError, streamAsk, type AskMsg, type AskTurn, type OpenAskOptions } from '../lib/ask';
import { showIntercom } from '../lib/intercom';

export default function AskProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [msgs, setMsgs] = useState<AskMsg[]>([]);
  const [busy, setBusy] = useState(false);
  const [caseNumber, setCaseNumber] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  /* Read inside send() so it always sees the latest conversation. */
  const msgsRef = useRef<AskMsg[]>([]);
  msgsRef.current = msgs;
  const busyRef = useRef(false);

  const send = useCallback(
    async (question: string, focusCase?: number | null) => {
      const q = question.trim();
      if (!q || busyRef.current) return;
      busyRef.current = true;
      setBusy(true);

      const history: AskTurn[] = [
        ...msgsRef.current.filter((m) => !m.error && m.content).map((m) => ({ role: m.role, content: m.content })),
        { role: 'user', content: q },
      ];
      setMsgs((prev) => [
        ...prev,
        { role: 'user', content: q },
        { role: 'assistant', content: '', streaming: true, status: null },
      ]);

      const patchLast = (fn: (m: AskMsg) => AskMsg) =>
        setMsgs((prev) => {
          if (!prev.length) return prev;
          const next = prev.slice();
          next[next.length - 1] = fn(next[next.length - 1]);
          return next;
        });

      const ac = new AbortController();
      abortRef.current = ac;
      try {
        await streamAsk(
          history,
          focusCase === undefined ? caseNumber : focusCase,
          (e) => {
            if (e.type === 'text') patchLast((m) => ({ ...m, content: m.content + e.text, status: null }));
            else if (e.type === 'status') patchLast((m) => ({ ...m, status: e.text }));
            else if (e.type === 'handoff') patchLast((m) => ({ ...m, handoff: e.message }));
            else if (e.type === 'error') patchLast((m) => ({ ...m, error: e.message }));
          },
          ac.signal,
        );
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          const status = err instanceof AskHttpError ? err.status : 0;
          const message =
            status === 429
              ? (err as Error).message
              : status === 503
                ? "Ask Agent Loop isn't switched on right now. A person can help instead."
                : "Sorry, I couldn't answer that just now. Try again, or message the team.";
          patchLast((m) => ({ ...m, error: message, handoff: m.handoff ?? q }));
        }
      } finally {
        patchLast((m) => ({ ...m, streaming: false, status: null, content: m.content.trim() }));
        busyRef.current = false;
        setBusy(false);
        abortRef.current = null;
      }
    },
    [caseNumber],
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    busyRef.current = false;
    setBusy(false);
    setMsgs([]);
    setCaseNumber(null);
  }, []);

  const open = useCallback(
    (opts?: OpenAskOptions) => {
      if (opts && 'caseNumber' in opts) setCaseNumber(opts.caseNumber ?? null);
      navigate('/ask');
      if (opts?.question) void send(opts.question, opts.caseNumber ?? null);
    },
    [navigate, send],
  );

  const talkToPerson = useCallback((prefill?: string | null) => {
    const last = [...msgsRef.current].reverse().find((m) => m.role === 'user')?.content;
    showIntercom(prefill || (last ? `${last} ` : undefined));
  }, []);

  const value = useMemo(
    () => ({
      open,
      msgs,
      busy,
      caseNumber,
      setCaseNumber,
      send: (q: string) => void send(q),
      reset,
      talkToPerson,
    }),
    [open, msgs, busy, caseNumber, send, reset, talkToPerson],
  );

  return <AskContext.Provider value={value}>{children}</AskContext.Provider>;
}
